import React, { useState, useRef, useEffect } from 'react';
import 'flatpickr/dist/themes/material_green.css';
import Flatpickr from 'react-flatpickr';
import Icon from 'shared/icons/IconComponent';
import RequiredMark from 'shared/inputs/RequiredMark';
import format from 'date-fns/format';
import get from 'lodash/get';
// Helpers
import cn from 'classnames';
import { dateFormatter, timeFormatter } from '../../constants/Formatting';
import { getDateInputFormat, getDateTimeInputFormat } from '../../helpers/date';

/**
 * @deprecated Use Telerik or shared components instead.
 */
const FormikDatePicker = ({
  wrapperClass,
  required = false,
  disabled = false,
  preventDefaultFormat = false,
  dateTimeFormat = false,
  showTime = false,
  label,
  name,
  value,
  onChange,
  onBlur,
  errors,
  setTouched,
  pickerOptions = {},
  validate = false,
  showBuiltInError = true,
  wrapperClassName,
}) => {
  const [textDate, setTextDate] = useState(value);
  const [pickerShowing, setPickerShowing] = useState(false);
  const [error, setError] = useState(false);

  const pickerInput = useRef();

  const updatePickerDate = (selected, string) => {
    // Don't fire an onChange if nothing actually changed.
    // Formik form `dirty` flag is useless otherwise
    if (format(value) === format(string)) {
      if (string) { //
        setTextDate(preventDefaultFormat ? format(string, 'MM/DD/YYYY HH:mm') : dateFormatter(string));
      }
      return;
    }
    if (string && !preventDefaultFormat) {
      setTextDate(dateFormatter(string));
      onChange(showTime ? getDateTimeInputFormat(string) : getDateInputFormat(string));
    } else if (string && preventDefaultFormat) {
      setTextDate(format(string, 'MM/DD/YYYY HH:mm'));
      onChange(showTime ? getDateTimeInputFormat(string) : getDateInputFormat(string));
    }
  };

  useEffect(() => {
    if (value) {
      updatePickerDate(null, value);
    } else if (textDate) {
      setTextDate(null);
    }
  }, [value]);

  const handleBlur = (e) => {
    const formatted = dateFormatter(e.target.value);
    if (e.target.value) {
      setError(false);

      if (preventDefaultFormat && dateTimeFormat) {
        const string = e.target.value;
        const formattedDate = dateFormatter(string.split(' ')[0]);
        const formattedTime = timeFormatter(string.split(' ')[1]);
        if (!formattedTime) {
          setError(true);
        }

        setTextDate(`${formattedDate || 'Invalid Date'} ${formattedTime || 'Invalid Time'}`);
        onChange(getDateTimeInputFormat(`${formattedDate} ${formattedTime}`));
        if (validate) {
          validate(`${formattedDate} ${formattedTime}`);
        }
      } else if (preventDefaultFormat && !dateTimeFormat) {
        setTextDate(format(e.target.value, 'MM/DD/YYYY HH:mm'));
        onChange(getDateTimeInputFormat(e.target.value));
      } else {
        setTextDate(formatted);
        onChange(getDateInputFormat(e.target.value));
      }
    } else if (required && !e.target.value && showBuiltInError) {
      setTextDate('');
      onChange('');
      setError('This Field is Required');
    } else {
      setTextDate('');
      onChange('');
      setError(false);
    }
    setTouched(name);
    onBlur(e, e.target.value);
  };

  const placeholder = showTime ? 'mm/dd/yyyy hh:mm' : 'mm/dd/yyyy';

  let errorDisplay = error;

  if (errors[name]) {
    errorDisplay = errors[name];
  } else {
    const er = get(errors, name);

    if (er) {
      errorDisplay = er;
    }
  }

  const wClassName = cn('gwi-datepicker-container', {
    [wrapperClass]: !!wrapperClass,
    [wrapperClassName]: !!wrapperClassName,
  });

  return (
    <div className={wClassName}>
      <label>
        {label}
        {required ? <RequiredMark /> : null}
      </label>
      <div className={`date-input-wrapper ${errors[name] || error || get(errors, name) ? 'error' : ''}`}>
        <input
          className='date-text-input'
          type='text'
          name={name}
          value={textDate || ''}
          onChange={(e) => setTextDate(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          maxLength={placeholder.length}
          required={required}
          disabled={disabled}
          autoComplete='off'
        />
        <Flatpickr
          disabled={disabled}
          ref={pickerInput}
          data-enable-time={showTime}
          time_24hr="true"
          value={value ? format(value, 'MM/DD/YYYY HH:mm') : ''}
          onChange={(selected, string) => updatePickerDate(selected, string)}
          className='picker'
          tabIndex='-1'
          options={{
            dateFormat: 'm/d/Y H:i',
            minuteIncrement: 1,
            disableMobile: true,
            ...pickerOptions,
          }}
          render={({ defaultValue, ...props }, ref) => (
            <div className='picker-input'>
              <input {...props} defaultValue={defaultValue} ref={ref} readOnly />
              <Icon
                onClick={() => {
                  if (pickerShowing && pickerInput.current && pickerInput.current.flatpickr) {
                    setPickerShowing(false);
                    pickerInput.current.flatpickr.close();
                  } else {
                    setPickerShowing(true);
                    pickerInput.current.flatpickr.open();
                  }
                }}
                icon='available'
                position='none'
                className='picker-icon font-size--large'
              />
            </div>
          )}
        />
      </div>
      {(errors[name] || error || get(errors, name)) && <div className='feedback'>{errorDisplay}</div>}
    </div>
  );
};

export default FormikDatePicker;
